// client/src/components/pages/WeighbridgeCompliance/NCRRegister.tsx
// (converted from NCRRegister.js, ST-00259)
//
// ST-00259: toasts now render (AppToaster), so the list's load failure carries a stable
// toastId: a filter change while on a later page reloads the list twice (once for the
// filter, once for the reset to page 1), and a failing endpoint used to stack two identical
// "Failed to load NCR records" per change. Nothing else changed.
import ThemedSelect from '../../common/ThemedSelect';
import { useState, useEffect, useCallback, type ComponentType } from 'react';
import type { AxiosResponse } from 'axios';
import {
  ClipboardList,
  Plus,
  AlertTriangle,
  CheckCircle,
  Clock,
  AlertCircle,
  Edit2,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { parseISO, isBefore, startOfDay } from 'date-fns';
import { formatAppDate } from '../../../utils/dateUtils';
import api from '../../../services/weighbridgeComplianceApi';
import { NCRModal as NCRModalJs } from './NCRModal';
import type {
  ComplianceAssetRow,
  NcrListParams,
  NcrRegisterStats,
  NcrRow,
  PaginatedBody,
} from '../../../types/clientPages/weighbridgeCompliance';

// NCRModal is still JS: a typed view of its props.
const NCRModal = NCRModalJs as unknown as ComponentType<{
  record: NcrRow | null;
  assets: ComplianceAssetRow[];
  onClose: () => void;
  onSaved: () => void;
}>;

const STATUS_OPTIONS = ['Open', 'InProgress', 'Resolved', 'Closed', 'Cancelled'];
const SEVERITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];
const CATEGORIES = [
  'Calibration Failure',
  'Verification Failure',
  'Maintenance Finding',
  'Suspect Reading',
  'Seal Breach',
  'Operator Non-Compliance',
  'Other',
];

const PAGE_SIZE = 20;

export const NCRRegister = () => {
  const [records, setRecords] = useState<NcrRow[]>([]);
  const [assets, setAssets] = useState<ComplianceAssetRow[]>([]);
  const [stats, setStats] = useState<NcrRegisterStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [assetFilter, setAssetFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<NcrRow | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const today = startOfDay(new Date());

  const fetchStats = useCallback(async () => {
    try {
      const response = (await api.ncr.getStats()) as AxiosResponse<NcrRegisterStats>;
      setStats(response.data || null);
    } catch (error) {
      console.error('Error fetching NCR stats:', error);
    }
  }, []);

  const fetchAssets = useCallback(async () => {
    try {
      const response = (await api.assets.getAll()) as AxiosResponse<
        PaginatedBody<ComplianceAssetRow> | ComplianceAssetRow[]
      >;
      const d = response.data;
      setAssets(Array.isArray(d) ? d : d?.data || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  }, []);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params: NcrListParams = { page, limit: PAGE_SIZE };
      if (assetFilter) params.assetId = assetFilter;
      if (statusFilter) params.status = statusFilter;
      if (severityFilter) params.severity = severityFilter;
      if (categoryFilter) params.category = categoryFilter;
      const response = (await api.ncr.getAll(params)) as AxiosResponse<PaginatedBody<NcrRow>>;
      const payload = response.data || {};
      setRecords(payload.data || []);
      setTotal(payload.total || 0);
      setTotalPages(payload.totalPages || 1);
    } catch (error) {
      // One message for the double reload a filter change makes (ST-00259).
      toast.error('Failed to load NCR records', { toastId: 'compliance-ncr-load' });
      console.error('Error fetching NCRs:', error);
    } finally {
      setLoading(false);
    }
  }, [page, assetFilter, statusFilter, severityFilter, categoryFilter]);

  useEffect(() => {
    fetchAssets();
    fetchStats();
  }, [fetchAssets, fetchStats]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [assetFilter, statusFilter, severityFilter, categoryFilter]);

  const handleDelete = async (id: number) => {
    try {
      await api.ncr.delete(id);
      toast.success('NCR deleted');
      setDeleteConfirm(null);
      fetchRecords();
      fetchStats();
    } catch {
      toast.error('Failed to delete NCR');
    }
  };

  const handleEdit = (record: NcrRow) => {
    setEditingRecord(record);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setEditingRecord(null);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingRecord(null);
  };

  const handleModalSaved = () => {
    handleModalClose();
    fetchRecords();
    fetchStats();
  };

  const isOverdue = (ncr: NcrRow) => {
    if (!['Open', 'InProgress'].includes(ncr.status ?? '')) return false;
    if (!ncr.due_date) return false;
    return isBefore(parseISO(ncr.due_date), today);
  };

  const getStatusBadge = (status: string | null | undefined) => {
    const key = status ?? '';
    const styles: Record<string, string> = {
      Open: 'bg-blue-50 text-blue-700 border-blue-200',
      InProgress: 'bg-amber-50 text-amber-700 border-amber-200',
      Resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      Closed: 'bg-slate-100 text-slate-500 border-slate-200',
      Cancelled: 'bg-red-50 text-red-600 border-red-200',
    };
    const dots: Record<string, string> = {
      Open: 'bg-blue-500',
      InProgress: 'bg-amber-500',
      Resolved: 'bg-emerald-500',
      Closed: 'bg-slate-400',
      Cancelled: 'bg-red-400',
    };
    const label = status === 'InProgress' ? 'In Progress' : status;
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${styles[key] || styles.Open}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${dots[key] || dots.Open}`} />
        {label}
      </span>
    );
  };

  const getSeverityBadge = (severity: string | null | undefined) => {
    const styles: Record<string, string> = {
      Critical: 'bg-red-50 text-red-700 border-red-200',
      High: 'bg-orange-50 text-orange-700 border-orange-200',
      Medium: 'bg-amber-50 text-amber-700 border-amber-200',
      Low: 'bg-blue-50 text-blue-700 border-blue-200',
    };
    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border ${styles[severity ?? ''] || styles.Medium}`}
      >
        {severity}
      </span>
    );
  };

  const getCategoryBadge = (category: string | null | undefined) => {
    const colors: Record<string, string> = {
      'Calibration Failure': 'bg-purple-50 text-purple-700',
      'Verification Failure': 'bg-indigo-50 text-indigo-700',
      'Maintenance Finding': 'bg-blue-50 text-blue-700',
      'Suspect Reading': 'bg-amber-50 text-amber-700',
      'Seal Breach': 'bg-red-50 text-red-700',
      'Operator Non-Compliance': 'bg-orange-50 text-orange-700',
      Other: 'bg-slate-100 text-slate-600',
    };
    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${colors[category ?? ''] || colors.Other}`}
      >
        {category}
      </span>
    );
  };

  const SkeletonRow = () => (
    <tr>
      {Array.from({ length: 9 }).map((_, i) => (
        <td key={i} className="px-5 py-3.5">
          <div
            className="h-4 bg-slate-200 rounded animate-pulse"
            style={{ width: `${50 + Math.random() * 50}%` }}
          />
        </td>
      ))}
    </tr>
  );

  const hasOverdue = (stats?.overdue_count ?? 0) > 0;
  const hasCriticalOpen = (stats?.critical_open ?? 0) > 0;

  const kpiCards = [
    {
      label: 'Open',
      value: stats?.open_count ?? '-',
      icon: <Clock className="w-5 h-5 text-blue-500" />,
      iconBg: 'bg-blue-50',
      highlight: false,
      valueCls: 'text-slate-800',
      borderCls: 'border-slate-200',
      cardBg: 'bg-surface',
    },
    {
      label: 'In Progress',
      value: stats?.in_progress_count ?? '-',
      icon: <AlertCircle className="w-5 h-5 text-amber-500" />,
      iconBg: 'bg-amber-50',
      highlight: false,
      valueCls: 'text-slate-800',
      borderCls: 'border-slate-200',
      cardBg: 'bg-surface',
    },
    {
      label: 'Overdue',
      value: stats?.overdue_count ?? '-',
      icon: (
        <AlertTriangle
          className={`w-5 h-5 ${hasOverdue ? 'text-red-500' : 'text-slate-400'}`}
        />
      ),
      iconBg: hasOverdue ? 'bg-red-50' : 'bg-slate-50',
      highlight: hasOverdue,
      valueCls: hasOverdue ? 'text-red-600' : 'text-slate-800',
      borderCls: hasOverdue ? 'border-red-200' : 'border-slate-200',
      cardBg: hasOverdue ? 'bg-red-50/30' : 'bg-surface',
    },
    {
      label: 'Critical Open',
      value: stats?.critical_open ?? '-',
      icon: (
        <AlertTriangle
          className={`w-5 h-5 ${hasCriticalOpen ? 'text-red-500' : 'text-slate-400'}`}
        />
      ),
      iconBg: hasCriticalOpen ? 'bg-red-50' : 'bg-slate-50',
      highlight: hasCriticalOpen,
      valueCls: hasCriticalOpen ? 'text-red-600' : 'text-slate-800',
      borderCls: hasCriticalOpen ? 'border-red-200' : 'border-slate-200',
      cardBg: hasCriticalOpen ? 'bg-red-50/30' : 'bg-surface',
    },
    {
      label: 'Resolved This Month',
      value: stats?.resolved_this_month ?? '-',
      icon: <CheckCircle className="w-5 h-5 text-emerald-500" />,
      iconBg: 'bg-emerald-50',
      highlight: false,
      valueCls: 'text-slate-800',
      borderCls: 'border-slate-200',
      cardBg: 'bg-surface',
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2
            className="text-lg font-bold text-slate-800"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Non-Conformance Register
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Track and manage weighbridge non-conformances
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-xs font-semibold flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" /> Raise NCR
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        {kpiCards.map((card) => (
          <div
            key={card.label}
            className={`rounded-xl border ${card.borderCls} ${card.cardBg} p-4 flex items-center gap-3`}
          >
            <div
              className={`w-10 h-10 rounded-lg ${card.iconBg} flex items-center justify-center flex-shrink-0`}
            >
              {card.icon}
            </div>
            <div>
              <p className={`text-2xl font-bold ${card.valueCls}`}>{card.value}</p>
              <p className="text-xs text-slate-500">{card.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative">
          <ThemedSelect
            value={assetFilter}
            onChange={(e) => setAssetFilter(String(e.target.value))}
            className="pl-3 pr-8 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm appearance-none bg-surface"
          >
            <option value="">All Assets</option>
            {assets.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name || String(a.asset_name ?? '')}
              </option>
            ))}
          </ThemedSelect>
        </div>

        <div className="relative">
          <ThemedSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(String(e.target.value))}
            className="pl-3 pr-8 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm appearance-none bg-surface"
          >
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s === 'InProgress' ? 'In Progress' : s}
              </option>
            ))}
          </ThemedSelect>
        </div>

        <div className="relative">
          <ThemedSelect
            value={severityFilter}
            onChange={(e) => setSeverityFilter(String(e.target.value))}
            className="pl-3 pr-8 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm appearance-none bg-surface"
          >
            <option value="">All Severities</option>
            {SEVERITY_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </ThemedSelect>
        </div>

        <div className="relative">
          <ThemedSelect
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(String(e.target.value))}
            className="pl-3 pr-8 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm appearance-none bg-surface"
          >
            <option value="">All Categories</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </ThemedSelect>
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50/80 border-b border-slate-100">
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest whitespace-nowrap">
                  NCR Number
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Asset
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Category
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Severity
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Title
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest whitespace-nowrap">
                  Assigned To
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest whitespace-nowrap">
                  Due Date
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Status
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={9}>
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
                        <ClipboardList className="w-8 h-8 text-slate-300" />
                      </div>
                      <h3 className="text-base font-semibold text-slate-700 mb-1">
                        No NCR records found
                      </h3>
                      <p className="text-sm text-slate-400 max-w-sm">
                        {assetFilter || statusFilter || severityFilter || categoryFilter
                          ? 'Try adjusting your filter criteria'
                          : 'Raise your first non-conformance to get started'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                records.map((ncr) => {
                  const overdue = isOverdue(ncr);
                  return (
                    <tr
                      key={ncr.id}
                      className={`hover:bg-primary-50/30 cursor-pointer transition-colors ${overdue ? 'bg-red-50/40' : ''}`}
                      onClick={() => handleEdit(ncr)}
                    >
                      <td className="px-5 py-3.5 text-sm font-mono text-slate-700 font-medium whitespace-nowrap">
                        {ncr.ncr_number || '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-700">
                        <div className="font-medium">{ncr.asset_name || '-'}</div>
                        {ncr.asset_location && (
                          <div className="text-xs text-slate-400">{ncr.asset_location}</div>
                        )}
                      </td>
                      <td className="px-5 py-3.5">{getCategoryBadge(ncr.category)}</td>
                      <td className="px-5 py-3.5">{getSeverityBadge(ncr.severity)}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-700 max-w-[220px]">
                        <span className="block truncate" title={ncr.title || ''}>
                          {ncr.title || '-'}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-700">
                        {ncr.assigned_to || '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm whitespace-nowrap">
                        {ncr.due_date ? (
                          <span
                            className={overdue ? 'text-red-600 font-semibold' : 'text-slate-700'}
                          >
                            {formatAppDate(ncr.due_date)}
                            {overdue && (
                              <AlertTriangle className="inline w-3.5 h-3.5 ml-1 text-red-500" />
                            )}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-5 py-3.5">{getStatusBadge(ncr.status)}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(ncr);
                            }}
                            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                            title="Edit NCR"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirm(ncr.id);
                            }}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                            title="Delete NCR"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100">
            <p className="text-xs text-slate-500">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}{' '}
              records
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-slate-500"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-slate-600 font-medium">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-slate-500"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-800">Delete NCR</h3>
                <p className="text-sm text-slate-500">This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm text-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NCR Modal */}
      {modalOpen && (
        <NCRModal
          record={editingRecord}
          assets={assets}
          onClose={handleModalClose}
          onSaved={handleModalSaved}
        />
      )}
    </div>
  );
};
